import React, {useCallback, useEffect, useMemo, useState} from "react";
import {getUserExperience} from "../../../../service/userService";
import BasicGrid from "../../../grid/BasicGrid";
import {Button, Divider, toaster} from "rsuite";
import SuccessNotification from "../../../notifications/SuccessNotification";
import {
    EXPERIENCE_ADD_ERROR,
    EXPERIENCE_ADD_SUCCESS,
    EXPERIENCE_EDIT_ERROR,
    EXPERIENCE_EDIT_SUCCESS,
} from "../../../../service/constants";
import ErrorNotification from "../../../notifications/ErrorNotification";
import {parseISO} from "rsuite/cjs/utils/dateUtils";
import AddExperienceModal from "../../../modals/experience/AddExperienceModal";
import EditExperienceModal from "../../../modals/experience/EditExperienceModal";

// isPresent is in the data, but is only used by the grid for rendering
const defaultColumns = [
    {
        key: 'position',
        label: 'Position',
        flexGrow: 2,
    },
    {
        key: 'company',
        label: 'Company',
        flexGrow: 2,
    },
    {
        key: 'startDate',
        label: 'Date Start',
    },
    {
        key: 'endDate',
        label: 'Date End',
    },
];

const UserEditExperience = (props) => {

    const { selectedUser } = props;

    const [loading, setLoading] = useState(false);
    const [userExperience, setUserExperience] = useState([]);

    // add modal
    const [addModalOpen, setAddModalOpen] = useState(false);

    // edit modal
    const [selectedExp, setSelectedExp] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);

    const loadUserExperience = useCallback(() => {
        setLoading(true);
        getUserExperience(selectedUser.id).then((experience) => {
            experience.forEach((exp) => {
                exp.startDate = parseISO(exp.startDate);
                exp.endDate = parseISO(exp.endDate);
            });
            experience.sort((a, b) => (a.endDate < b.endDate) ? 1 : -1);
            setUserExperience(experience);
            setLoading(false);
        });
    }, [selectedUser]);

    useEffect(() => loadUserExperience(), [loadUserExperience]);

    const handleAddClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={EXPERIENCE_ADD_SUCCESS} />, { placement: 'topCenter' });
            loadUserExperience();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={EXPERIENCE_ADD_ERROR} />, { placement: 'topCenter' });
        }
        setAddModalOpen(false);
    },[loadUserExperience]);

    const handleEditClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={EXPERIENCE_EDIT_SUCCESS} />, { placement: 'topCenter' });
            loadUserExperience();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={EXPERIENCE_EDIT_ERROR} />, { placement: 'topCenter' });
        }
        setEditModalOpen(false);
    }, [loadUserExperience]);

    // table
    const [columnKeys] = useState(defaultColumns.map(column => column.key));
    const columns = defaultColumns.filter(column => columnKeys.some(key => key === column.key));

    const setClickedExperience = useCallback((rowData) => {
        setEditModalOpen(true);
        setSelectedExp(rowData);
    },[]);

    return useMemo(() => {
        return (
            <div className="main-content">
                <Button appearance="primary" onClick={() => setAddModalOpen(true)}>Add Experience</Button>
                <Divider />
                <BasicGrid
                    loading={loading}
                    columns={columns}
                    data={userExperience}
                    handleClick={setClickedExperience}
                    exportTitle={`admin-edit-${selectedUser.lastName}-${selectedUser.firstName}-experience`}
                />
                <AddExperienceModal
                    key={`add-exp-modal-${selectedUser.id}`}
                    open={addModalOpen}
                    handleClose={handleAddClose}
                    userId={selectedUser.id}
                />
                <EditExperienceModal
                    key={`edit-exp-modal-${selectedUser.id}`}
                    open={editModalOpen}
                    handleClose={handleEditClose}
                    experience={selectedExp}
                    userId={selectedUser.id}
                />
            </div>
        );
    }, [
        addModalOpen,
        columns,
        editModalOpen,
        handleAddClose,
        handleEditClose,
        loading,
        selectedExp,
        setClickedExperience,
        userExperience,
        selectedUser.id,
        selectedUser.firstName,
        selectedUser.lastName,
    ]);
}

export default UserEditExperience;